import React from 'react';
import {useTranslation} from "react-i18next";
import Money from "../../../../../../../components/Common/Money";
import {ReactComponent as MenuItemIco} from "../../../../../../../../images/icons/menus/menu-item.svg";
import {ReactComponent as MenuItemDarkIco} from "../../../../../../../../images/icons/menus/menu-item-dark.svg";
import {ReactComponent as CheckCircleIco} from "../../../../../../../../images/icons/check-circle.svg";
import {ReactComponent as PlusIco} from "../../../../../../../../images/icons/plus.svg";
import ProductModifierGroupAdvancedSubOptions from "./ProductModifierGroupAdvancedSubOptions";
import AppConfig from "../../../../../../../services/AppConfig";

interface Props {
    modifierGroupOption: Record<string, any>;
    handleAddSubOption: (option: Record<string, any>) => void;
    adjustOption: (optionState: Record<string, any>, stateIndex: number) => void;
    removeOption: (optionState: Record<string, any>, stateIndex: number) => void;
    modifierGroup: Record<string, any>;
    changeOptionQuantity: (quantity: number, option: Record<string, any>) => void;
}

const ProductModifierGroupAdvancedOption = ({
                                                modifierGroupOption,
                                                handleAddSubOption,
                                                adjustOption,
                                                removeOption,
                                                modifierGroup,
                                                changeOptionQuantity
                                            }: Props) => {
    const quantity = modifierGroupOption?.quantity ?? 0;
    const displayQuantity = quantity - (modifierGroupOption.min_permitted ?? 0);
    const checked = displayQuantity > 0;
    const {t} = useTranslation();
    const disabled = modifierGroupOption.disabled;

    const isIncreaseQuantityDisabled = () => {
        if (modifierGroup.max_permitted && modifierGroup.max_permitted <= modifierGroup.quantity) return true;
        if (modifierGroupOption.max_permitted && modifierGroupOption.max_permitted <= quantity) return true;
    }

    const handleSelect = () => {
        if (disabled || quantity || isIncreaseQuantityDisabled()) return;

        changeOptionQuantity(1, modifierGroupOption)
    };

    const addOption = () => {
        handleAddSubOption(modifierGroupOption);
    }

    const getTotalPrice = () => {
        if (modifierGroupOption.states.length === 0) return modifierGroupOption.price;

        return modifierGroupOption.states.reduce((acc: number, state: Record<string, any>) => acc + state.total_price, 0);
    }

    const isDisabled = () => {
        if (!quantity && isIncreaseQuantityDisabled()) return true;

        return modifierGroupOption.disabled;
    }

    return (
        <div onClick={handleSelect}
             className={`product-item-option product-modifier-group-option product-modifier-group-option-advanced ${checked ? "active" : ""} ${isDisabled() ? "disabled" : ""}`}>
            {checked && !modifierGroupOption.disabled && (
                <div className="check-circle">
                    <CheckCircleIco/>
                </div>
            )}
            <div className="d-flex">
                <div className="product-image">
                    {modifierGroupOption.image_link ? (
                        <img alt={t("common.word.product_image")}
                             src={modifierGroupOption.image_link.default}/>
                    ) : (
                        AppConfig.isDarkMode() ? <MenuItemDarkIco/> : <MenuItemIco/>
                    )}
                </div>
                <div className="d-flex flex-column w-100">
                    {disabled &&
                        <small
                            className="text-warning fw-normal">{t("modules.menu.product_not_available.field")}</small>}
                    <h1 className="h6">
                        {modifierGroupOption.name}
                    </h1>
                    {modifierGroupOption.description && (
                        <p className="fw-normal">{modifierGroupOption.description}</p>
                    )}
                    <div className="d-flex justify-content-between align-items-center gap-3 w-100">
                        <p className="price">
                            <Money
                                value={getTotalPrice()}
                                currency='PLN'/>
                        </p>
                        {quantity >= 1 && (
                            <button type="button" className="btn btn-default btn-icon"
                                    onClick={addOption}
                                    disabled={isIncreaseQuantityDisabled()}
                                    aria-label={`${t("common.action.add")} ${modifierGroupOption.name}`}>
                                <PlusIco aria-hidden="true"/>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {
                modifierGroupOption.modifier_groups.length > 0 && modifierGroupOption.states.length > 0 && (
                    <div className="product-modifier-group-option-advanced-sub-options">
                        <ProductModifierGroupAdvancedSubOptions
                            modifierGroupOption={modifierGroupOption}
                            options={modifierGroupOption.modifier_groups || []}
                            handleAdjustOption={adjustOption}
                            handleRemoveOption={removeOption}
                        />
                    </div>
                )
            }
        </div>
    )
}

export default ProductModifierGroupAdvancedOption;
